import { useCallback, useEffect, useMemo, useState } from 'react';
import GridTable from '../../table/GridTable';
import MobileAccordionTable from '../../table/MobileAccordionTable';
import MobileFilterTabs from '../../filter/MobileFilterTabs';
import { ColDef, RowClassParams } from 'ag-grid-community';
import {
  usePurchaseLandingRequestStore,
  IPurchaseLandingRequest,
  PurchaseLandingRequestStatus,
} from '../../../store/usePurchaseLandingRequestStore';
import { useFranchiseStore } from '../../../store/useFranchise';
import { useLoginStore } from '../../../store/useLogin';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faSearch,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import Input from '../../form/Input';
import DatePickerInput from '../../form/DatePickerInput';
import PurchaseLandingConfirmModal from './PurchaseLandingConfirmModal';
import { toast } from 'react-toastify';
import useIsMobile from '../../../hook/useIsMobile';
import { formatDate } from '../../../util/util';

// 확인 모달 상태 타입
interface ConfirmModalState {
  isOpen: boolean;
  title: string;
  message: string;
  subMessage?: string;
  confirmText?: string;
  confirmButtonClass?: string;
  onConfirm: () => Promise<void>;
}

const STATUS_CONFIG: Record<
  PurchaseLandingRequestStatus,
  { label: string; bgColor: string; textColor: string }
> = {
  PENDING_PAYMENT: { label: '미입금', bgColor: '!bg-orange-100', textColor: '!text-orange-800' },
  ORDER_CREATED: { label: '주문생성', bgColor: '!bg-blue-100', textColor: '!text-blue-800' },
};

const PurchaseLandingRequestPage = () => {
  const {
    requestList,
    requestPage,
    statistics,
    loading,
    setSearch,
    fetchRequests,
    fetchStatistics,
    confirmPayment,
    sendAlimtalk,
    deleteRequest,
  } = usePurchaseLandingRequestStore();
  const { role } = useLoginStore();
  const { allFranchises, getAllFranchises } = useFranchiseStore();
  const isMobile = useIsMobile();

  const [selectedRequest, setSelectedRequest] = useState<IPurchaseLandingRequest | null>(null);
  const [isDetailOpen, setDetailOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchStatus, setSearchStatus] = useState<PurchaseLandingRequestStatus | ''>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedFranchiseIds, setSelectedFranchiseIds] = useState<string[]>([]);
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  
  // 모바일 선택 상태
  const [mobileSelectedKeys, setMobileSelectedKeys] = useState<Set<string | number>>(new Set());
  const [mobileSelectedRows, setMobileSelectedRows] = useState<IPurchaseLandingRequest[]>([]);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  const closeConfirmModal = useCallback(() => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    setIsConfirmLoading(false);
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStatistics();
  }, [fetchRequests, fetchStatistics]);

  useEffect(() => {
    if (role === 'ROLE_ADMIN') {
      getAllFranchises();
    }
  }, [getAllFranchises, role]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.purchase-request-franchise-dropdown')) {
        setShowFranchiseDropdown(false);
        setFranchiseSearchText('');
      }
    };

    if (showFranchiseDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFranchiseDropdown]);

  const handleSearch = useCallback(() => {
    setSearch({
      name: searchName.trim() || undefined,
      phone: searchPhone.trim() || undefined,
      status: searchStatus || undefined,
      franchiseIds: selectedFranchiseIds.length ? selectedFranchiseIds : undefined,
      startDt: startDate ? formatDate(startDate)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      endDt: endDate ? `${formatDate(endDate)['yyyy-mm-dd']}T23:59:59` : undefined,
    });
    fetchRequests(0, pageSize);
    fetchStatistics();
  }, [fetchRequests, fetchStatistics, searchName, searchPhone, searchStatus, selectedFranchiseIds, startDate, endDate, pageSize, setSearch]);

  const handleReset = useCallback(() => {
    setSearchName('');
    setSearchPhone('');
    setSearchStatus('');
    setStartDate(null);
    setEndDate(null);
    setSelectedFranchiseIds([]);
    setFranchiseSearchText('');
    setShowFranchiseDropdown(false);
    setSearch({});
    fetchRequests(0, pageSize);
    fetchStatistics();
  }, [setSearch, fetchRequests, fetchStatistics, pageSize]);

  const handleToggleFranchise = useCallback((franchiseId: string) => {
    setSelectedFranchiseIds(prev =>
      prev.includes(franchiseId)
        ? prev.filter(id => id !== franchiseId)
        : [...prev, franchiseId]
    );
  }, []);

  const filteredFranchises = useMemo(
    () =>
      allFranchises.filter(franchise =>
        franchise.companyName?.toLowerCase().includes(franchiseSearchText.toLowerCase())
      ),
    [allFranchises, franchiseSearchText]
  );

  const selectedFranchiseLabel = useMemo(() => {
    if (selectedFranchiseIds.length === 0) {
      return '전체 가맹점';
    }
    if (selectedFranchiseIds.length === 1) {
      return allFranchises.find(franchise => franchise.id === selectedFranchiseIds[0])?.companyName || '가맹점';
    }
    return `${selectedFranchiseIds.length}개 선택`;
  }, [allFranchises, selectedFranchiseIds]);

  const handlePagination = useCallback(
    (page: number) => {
      fetchRequests(page, pageSize);
    },
    [fetchRequests, pageSize]
  );

  // 입금확인 + 입금완료 알림톡 (동일한 API 호출)
  const handleConfirmPayment = useCallback(
    (id: number, isPendingPayment: boolean) => {
      setConfirmModal({
        isOpen: true,
        title: isPendingPayment ? '입금확인' : '알림톡 재발송',
        message: isPendingPayment
          ? '입금 확인 처리하시겠습니까?'
          : '입금완료 알림톡을 재발송하시겠습니까?',
        subMessage: isPendingPayment ? '• 입금완료 알림톡이 함께 발송됩니다.\n• 주문이 생성되어 [주문 대기]에 추가됩니다.\n• 이미 주문생성된 건은 처리되지 않습니다.' : undefined,
        confirmText: isPendingPayment ? '입금확인' : '재발송',
        confirmButtonClass: '!bg-green-600 hover:!bg-green-700',
        onConfirm: async () => {
          setIsConfirmLoading(true);
          const success = await confirmPayment(id);
          if (success) {
            const currentPage = requestPage?.pageable?.pageNumber ?? 0;
            await fetchRequests(currentPage);
          }
          closeConfirmModal();
        },
      });
    },
    [confirmPayment, fetchRequests, requestPage, closeConfirmModal]
  );

  // 미입금 알림톡만 처리 (UNPAID1, UNPAID2)
  const handleSendUnpaidAlimtalk = useCallback(
    (id: number, type: 'UNPAID1' | 'UNPAID2') => {
      const labels: Record<string, string> = { UNPAID1: '미입금 알림톡', UNPAID2: '미입금 재발송 알림톡' };
      const request = requestList.find(r => r.id === id);
      const firstSentAt = request?.unpaidAlimtalk1SentAt
        ? new Date(request.unpaidAlimtalk1SentAt).toLocaleString()
        : null;

      setConfirmModal({
        isOpen: true,
        title: '알림톡 발송',
        message: `${labels[type]}을 발송하시겠습니까?`,
        subMessage: type === 'UNPAID2' && firstSentAt ? `• 1차 발송: ${firstSentAt}` : undefined,
        confirmText: '발송',
        confirmButtonClass: '!bg-orange-500 hover:!bg-orange-600',
        onConfirm: async () => {
          setIsConfirmLoading(true);
          const success = await sendAlimtalk(id, type);
          if (success) {
            const currentPage = requestPage?.pageable?.pageNumber ?? 0;
            await fetchRequests(currentPage);
          }
          closeConfirmModal();
        },
      });
    },
    [sendAlimtalk, fetchRequests, requestPage, requestList, closeConfirmModal]
  );


  const handleViewDetail = useCallback((request: IPurchaseLandingRequest) => {
    setSelectedRequest(request);
    setDetailOpen(true);
  }, []);

  // 체크박스 토글
  const handleToggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // 전체 선택 (미입금 항목만)
  const selectableRequests = useMemo(
    () => requestList.filter(r => r.status === 'PENDING_PAYMENT'),
    [requestList]
  );

  const isAllSelected = useMemo(
    () => selectableRequests.length > 0 && selectableRequests.every(r => selectedIds.has(r.id)),
    [selectableRequests, selectedIds]
  );

  const handleToggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // 전체 해제
      setSelectedIds(new Set());
    } else {
      // 미입금 항목만 전체 선택
      setSelectedIds(new Set(selectableRequests.map(r => r.id)));
    }
  }, [isAllSelected, selectableRequests]);

  // 일괄 입금확인 (미입금 상태만 처리, 나머지는 스킵)
  const handleBulkConfirmPayment = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.warning('선택된 항목이 없습니다.');
      return;
    }
    // 미입금 상태인 것만 필터링
    const pendingIds = requestList
      .filter(r => selectedIds.has(r.id) && r.status === 'PENDING_PAYMENT')
      .map(r => r.id);

    if (pendingIds.length === 0) {
      toast.warning('미입금 상태인 항목이 없습니다.');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '일괄 입금확인',
      message: `${pendingIds.length}건의 입금을 확인 처리하시겠습니까?`,
      subMessage: '• 입금완료 알림톡이 함께 발송됩니다.\n• 생성된 주문은 [주문 대기]에서 확인 가능합니다.',
      confirmText: '입금확인',
      confirmButtonClass: '!bg-green-600 hover:!bg-green-700',
      onConfirm: async () => {
        setIsConfirmLoading(true);
        setIsBulkProcessing(true);
        let successCount = 0;
        for (const id of pendingIds) {
          const success = await confirmPayment(id);
          if (success) successCount++;
        }
        setIsBulkProcessing(false);
        setSelectedIds(new Set());

        if (successCount > 0) {
          const currentPage = requestPage?.pageable?.pageNumber ?? 0;
          await fetchRequests(currentPage);
          toast.success(`${successCount}건 입금확인 완료`);
        }
        closeConfirmModal();
      },
    });
  }, [selectedIds, requestList, confirmPayment, fetchRequests, requestPage, closeConfirmModal]);

  // 일괄 삭제
  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.warning('선택된 항목이 없습니다.');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: '일괄 삭제',
      message: `${ids.length}건의 신청을 삭제하시겠습니까?`,
      subMessage: '• 삭제된 신청은 복구할 수 없습니다.',
      confirmText: '삭제',
      confirmButtonClass: '!bg-red-600 hover:!bg-red-700',
      onConfirm: async () => {
        setIsConfirmLoading(true);
        setIsBulkProcessing(true);
        let successCount = 0;
        for (const id of ids) {
          const success = await deleteRequest(id);
          if (success) successCount++;
        }
        setIsBulkProcessing(false);
        setSelectedIds(new Set());

        if (successCount > 0) {
          const currentPage = requestPage?.pageable?.pageNumber ?? 0;
          await fetchRequests(currentPage);
          toast.success(`${successCount}건 삭제 완료`);
        }
        closeConfirmModal();
      },
    });
  }, [selectedIds, deleteRequest, fetchRequests, requestPage, closeConfirmModal]);

  const columnDefs = useMemo<ColDef<IPurchaseLandingRequest>[]>(() => {
    const columns: ColDef<IPurchaseLandingRequest>[] = [
      // 1. 체크박스
      {
        headerName: '',
        width: 50,
        cellRenderer: (params: any) => {
          const request: IPurchaseLandingRequest | undefined = params.data;
          if (!request) return null;
          const isOrderCreated = request.status === 'ORDER_CREATED';
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              <input
                type="checkbox"
                checked={selectedIds.has(request.id)}
                onChange={() => handleToggleSelect(request.id)}
                disabled={isOrderCreated}
                style={{ width: '16px', height: '16px', cursor: isOrderCreated ? 'not-allowed' : 'pointer', opacity: isOrderCreated ? 0.5 : 1 }}
                title={isOrderCreated ? '주문생성 완료된 항목은 선택할 수 없습니다' : undefined}
              />
            </div>
          );
        },
        headerComponent: () => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleToggleSelectAll}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              title="전체 선택 (미입금 항목만)"
            />
          </div>
        ),
      },
    ];

    // 2. 가맹점 (ADMIN 전용)
    if (role === 'ROLE_ADMIN') {
      columns.push({
        headerName: '가맹점',
        field: 'companyName' as const,
        width: 120,
        valueFormatter: (params: any) => params.value ?? '-',
      });
    }

    // 3. 상태
    columns.push({
      headerName: '상태',
      field: 'status',
      width: 80,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        if (!request) return '-';
        const config = STATUS_CONFIG[request.status];
        return (
          <div className="!flex !items-center !h-full">
            <span
              className={`!px-2 !py-1 !rounded !text-xs !font-semibold ${config.bgColor} ${config.textColor}`}
            >
              {config.label}
            </span>
          </div>
        );
      },
    });

    // 4. 신청일시
    columns.push({
      headerName: '신청일시',
      field: 'createdAt',
      width: 160,
      valueFormatter: params =>
        params.value ? new Date(params.value).toLocaleString() : '',
    });

    // 5. 이름 (custInfos 모든 이름 표시)
    columns.push({
      headerName: '이름',
      width: 130,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        if (!request) return '-';
        const custInfos = request.custInfos ?? [];
        if (custInfos.length === 0) return request.name ?? '-';
        const names = custInfos.map(c => c.name).join(', ');
        return (
          <div className="!flex !items-center !h-full" title={names}>
            <span className="!truncate">{names}</span>
          </div>
        );
      },
    });

    // 6. 연락처
    columns.push({
      headerName: '연락처',
      field: 'phone',
      width: 130,
    });

    // 7. 금액
    columns.push({
      headerName: '금액',
      field: 'packagePrice',
      width: 100,
      valueFormatter: params =>
        params.value ? `${params.value.toLocaleString()}원` : '-',
    });

    // 8. 상품
    columns.push({
      headerName: '상품',
      minWidth: 150,
      flex: 2,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        if (!request) return '-';
        const productNames = request.productNames ?? [];
        if (productNames.length === 0) return '-';
        return (
          <div
            className="!flex !gap-1 !items-center !h-full"
            style={{ overflow: 'hidden' }}
            title={productNames.join(', ')}
          >
            {productNames.map((name: string, idx: number) => (
              <span
                key={idx}
                className="!px-2 !py-0.5 !rounded !text-xs !bg-white !text-gray-700 !border !border-gray-300 !whitespace-nowrap !flex-shrink-0"
              >
                {name}
              </span>
            ))}
          </div>
        );
      },
    });

    // 9. 미입금 알림톡
    columns.push({
      headerName: '미입금 알림톡',
      width: 120,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        if (!request) return null;
        const { unpaidAlimtalk1SentAt, unpaidAlimtalk2SentAt } = request;

        // 2차까지 발송 완료
        if (unpaidAlimtalk1SentAt && unpaidAlimtalk2SentAt) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%', height: '100%' }}>
              <FontAwesomeIcon icon={faCheck} className="!text-green-500" />
              <FontAwesomeIcon icon={faCheck} className="!text-green-500" />
            </div>
          );
        }

        // 1차만 발송 완료 → 재발송 버튼
        if (unpaidAlimtalk1SentAt) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%', height: '100%' }}>
              <FontAwesomeIcon icon={faCheck} className="!text-green-500" />
              <button
                className="!px-2 !py-1 !rounded !text-xs !bg-orange-500 !text-white hover:!bg-orange-600"
                onClick={() => handleSendUnpaidAlimtalk(request.id, 'UNPAID2')}
                title="미입금 재발송"
              >
                재발송
              </button>
            </div>
          );
        }

        // 미발송 → 발송 버튼
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <button
              className="!px-2 !py-1 !rounded !text-xs !bg-orange-500 !text-white hover:!bg-orange-600"
              onClick={() => handleSendUnpaidAlimtalk(request.id, 'UNPAID1')}
              title="미입금 알림톡 발송"
            >
              <FontAwesomeIcon icon={faBell} className="!mr-1" />
              발송
            </button>
          </div>
        );
      },
    });

    // 10. 캠페인 (랜딩 삭제된 경우 표시)
    columns.push({
      headerName: '캠페인',
      field: 'campaignName',
      flex: 1.5,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        const name = params.value ?? '-';
        if (!request?.landingDeleted) return <span>{name}</span>;
        return (
          <span className="!text-gray-500">
            {name} <span className="!text-xs !text-red-500">(삭제됨)</span>
          </span>
        );
      },
    });

    // 11. 상세
    columns.push({
      headerName: '상세',
      width: 80,
      cellRenderer: (params: any) => {
        const request: IPurchaseLandingRequest | undefined = params.data;
        if (!request) return null;
        return (
          <div className="!flex !items-center !h-full">
            <button
              className="!px-2 !py-1 !rounded !text-xs !bg-gray-200 !text-gray-700 hover:!bg-gray-300"
              onClick={() => handleViewDetail(request)}
            >
              상세보기
            </button>
          </div>
        );
      },
    });

    return columns;
  }, [handleSendUnpaidAlimtalk, handleViewDetail, selectedIds, handleToggleSelect, handleToggleSelectAll, isAllSelected, role]);

  // 모바일 선택 개수 (미입금 상태만)
  const mobileSelectedCount = useMemo(() => {
    return mobileSelectedRows.filter(r => r.status === 'PENDING_PAYMENT').length;
  }, [mobileSelectedRows]);

  // 모바일 검색 폼
  const mobileSearchContent = (
      <div className="!p-3 !space-y-3">
      {role === 'ROLE_ADMIN' && (
        <div className="purchase-request-franchise-dropdown !relative">
          <button
            type="button"
            className="!w-full !h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm !bg-white !text-left !flex !items-center !justify-between"
            onClick={() => setShowFranchiseDropdown(prev => !prev)}
          >
            <span className="!truncate">{selectedFranchiseLabel}</span>
            <span className="!text-xs !text-gray-500">▼</span>
          </button>
          {showFranchiseDropdown && (
            <div className="!absolute !left-0 !right-0 !mt-1 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-20">
              <div className="!p-2 !border-b !border-gray-200">
                <input
                  type="text"
                  className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md !text-sm"
                  placeholder="가맹점 검색"
                  value={franchiseSearchText}
                  onChange={e => setFranchiseSearchText(e.target.value)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <div className="!max-h-56 !overflow-y-auto">
                {filteredFranchises.map(franchise => (
                  <label
                    key={franchise.id}
                    className="!flex !items-center !gap-2 !px-3 !py-2 hover:!bg-gray-50 !text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFranchiseIds.includes(franchise.id)}
                      onChange={() => handleToggleFranchise(franchise.id)}
                    />
                    <span className="!truncate">{franchise.companyName}</span>
                  </label>
                ))}
                {filteredFranchises.length === 0 && (
                  <div className="!px-3 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                )}
              </div>
              <div className="!flex !justify-between !px-3 !py-2 !border-t !border-gray-200">
                <button
                  type="button"
                  className="!text-xs !text-red-600"
                  onClick={() => setSelectedFranchiseIds([])}
                >
                  전체 해제
                </button>
                <button
                  type="button"
                  className="!text-xs !text-blue-600"
                  onClick={() => setShowFranchiseDropdown(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="!flex !gap-2">
        <div className="!flex-1">
          <label className="!block !text-xs !text-gray-500 !mb-1">시작일</label>
          <DatePickerInput
            value={startDate}
            onChange={setStartDate}
            placeholder="시작일"
          />
        </div>
        <div className="!flex-1">
          <label className="!block !text-xs !text-gray-500 !mb-1">종료일</label>
          <DatePickerInput
            value={endDate}
            onChange={setEndDate}
            placeholder="종료일"
          />
        </div>
      </div>
      <select
        className="!w-full !h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm"
        value={searchStatus}
        onChange={e => setSearchStatus(e.target.value as PurchaseLandingRequestStatus | '')}
      >
        <option value="">전체 상태</option>
        <option value="PENDING_PAYMENT">미입금</option>
        <option value="ORDER_CREATED">주문생성</option>
      </select>
      <div className="!flex !gap-2">
        <input
          type="text"
          className="!flex-1 !px-3 !py-2 !border !rounded !text-sm"
          placeholder="이름"
          value={searchName}
          onChange={e => setSearchName(e.target.value)}
        />
        <input
          type="text"
          className="!flex-1 !px-3 !py-2 !border !rounded !text-sm"
          placeholder="연락처"
          value={searchPhone}
          onChange={e => setSearchPhone(e.target.value)}
        />
      </div>
      <div className="!flex !gap-2">
        <button
          className="!flex-1 !py-2 !rounded !bg-blue-600 !text-white !text-sm hover:!bg-blue-700"
          onClick={handleSearch}
        >
          검색
        </button>
        <button
          className="!flex-1 !py-2 !rounded !bg-gray-200 !text-gray-700 !text-sm hover:!bg-gray-300"
          onClick={handleReset}
        >
          초기화
        </button>
      </div>
    </div>
  );

  return (
    <div className="!w-full !h-full !flex !flex-col">
      {/* 모바일 필터 */}
      <div className="md:!hidden !py-2">
        <MobileFilterTabs
          tabs={[{ id: 'search', label: '검색', content: mobileSearchContent }]}
        />
      </div>

      {/* 데스크톱 검색 필터 */}
      <div className="!hidden md:!block !bg-white !p-4 !rounded-lg !shadow-sm !mb-4">
        <div className="!flex !justify-between !items-end !gap-4">
          <div className="!flex !flex-wrap !gap-3 !items-end">
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">시작일</label>
            <DatePickerInput
              value={startDate}
              onChange={setStartDate}
              placeholder="시작일"
            />
          </div>
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">종료일</label>
            <DatePickerInput
              value={endDate}
              onChange={setEndDate}
              placeholder="종료일"
            />
          </div>
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">상태</label>
            <select
              className="!h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm"
              value={searchStatus}
              onChange={e => setSearchStatus(e.target.value as PurchaseLandingRequestStatus | '')}
            >
              <option value="">전체</option>
              <option value="PENDING_PAYMENT">미입금</option>
              <option value="ORDER_CREATED">주문생성</option>
            </select>
          </div>
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">이름</label>
            <Input
              value={searchName}
              onChange={e => setSearchName(e.target.value)}
              placeholder="신청자 이름"
              width="150px"
              textAlign="left"
            />
          </div>
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">연락처</label>
            <Input
              value={searchPhone}
              onChange={e => setSearchPhone(e.target.value)}
              placeholder="010-0000-0000"
              width="150px"
              textAlign="left"
            />
          </div>
          {role === 'ROLE_ADMIN' && (
            <div className="purchase-request-franchise-dropdown !relative">
              <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">가맹점</label>
              <button
                type="button"
                className="!h-10 !min-w-[180px] !max-w-[220px] !px-3 !border !border-gray-300 !rounded-md !text-sm !bg-white !text-left !flex !items-center !justify-between"
                onClick={() => setShowFranchiseDropdown(prev => !prev)}
              >
                <span className="!truncate">{selectedFranchiseLabel}</span>
                <span className="!text-xs !text-gray-500">▼</span>
              </button>
              {showFranchiseDropdown && (
                <div className="!absolute !left-0 !mt-1 !w-72 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-20">
                  <div className="!p-2 !border-b !border-gray-200">
                    <input
                      type="text"
                      className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-md !text-sm"
                      placeholder="가맹점 검색"
                      value={franchiseSearchText}
                      onChange={e => setFranchiseSearchText(e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                  <div className="!max-h-64 !overflow-y-auto">
                    {filteredFranchises.map(franchise => (
                      <label
                        key={franchise.id}
                        className="!flex !items-center !gap-2 !px-3 !py-2 hover:!bg-gray-50 !text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedFranchiseIds.includes(franchise.id)}
                          onChange={() => handleToggleFranchise(franchise.id)}
                        />
                        <span className="!truncate">{franchise.companyName}</span>
                      </label>
                    ))}
                    {filteredFranchises.length === 0 && (
                      <div className="!px-3 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                    )}
                  </div>
                  <div className="!flex !justify-between !px-3 !py-2 !border-t !border-gray-200">
                    <button
                      type="button"
                      className="!text-xs !text-red-600"
                      onClick={() => setSelectedFranchiseIds([])}
                    >
                      전체 해제
                    </button>
                    <button
                      type="button"
                      className="!text-xs !text-blue-600"
                      onClick={() => setShowFranchiseDropdown(false)}
                    >
                      닫기
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            className="!h-10 !px-4 !bg-blue-600 !text-white !rounded-md hover:!bg-blue-700 !flex !items-center !gap-2"
            onClick={handleSearch}
          >
            <FontAwesomeIcon icon={faSearch} />
            검색
          </button>
          <button
            className="!h-10 !px-4 !bg-gray-200 !text-gray-700 !rounded-md hover:!bg-gray-300"
            onClick={handleReset}
          >
            초기화
          </button>
          <div>
            <label className="!block !text-sm !font-medium !text-gray-700 !mb-1">페이지 크기</label>
            <select
              className="!h-10 !px-3 !border !border-gray-300 !rounded-md !text-sm"
              value={pageSize}
              onChange={e => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                fetchRequests(0, newSize);
              }}
            >
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </select>
          </div>
          </div>
          {/* 통계 */}
          {statistics && (
            <div className="!flex !flex-col !gap-1 !text-sm !whitespace-nowrap">
              <span className="!text-blue-600">
                주문생성({statistics.orderCreatedCount}): <span className="!font-semibold">{statistics.orderCreatedAmount.toLocaleString()}원</span>
              </span>
              <span className="!text-orange-600">
                미입금({statistics.pendingPaymentCount}): <span className="!font-semibold">{statistics.pendingPaymentAmount.toLocaleString()}원</span>
              </span>
            </div>
          )}
        </div>
        <div className="!flex !flex-wrap !gap-3 !items-end !mt-3">
          {/* 일괄 입금확인/삭제 버튼 */}
          {selectedIds.size > 0 && (
            <>
              <button
                className="!h-10 !px-4 !bg-green-600 !text-white !rounded-md hover:!bg-green-700 !flex !items-center !gap-2 disabled:!opacity-50"
                onClick={handleBulkConfirmPayment}
                disabled={isBulkProcessing}
              >
                <FontAwesomeIcon icon={faCheck} />
                {isBulkProcessing ? '처리중...' : `선택 입금확인 (${selectedIds.size}건)`}
              </button>
              <button
                className="!h-10 !px-4 !bg-red-600 !text-white !rounded-md hover:!bg-red-700 !flex !items-center !gap-2 disabled:!opacity-50"
                onClick={handleBulkDelete}
                disabled={isBulkProcessing}
              >
                <FontAwesomeIcon icon={faTrash} />
                {isBulkProcessing ? '처리중...' : `선택 삭제 (${selectedIds.size}건)`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* 테이블 */}
      <div className="md:!flex-1 md:!min-h-0">
        {isMobile ? (
          <MobileAccordionTable<IPurchaseLandingRequest>
            data={requestList}
            loading={loading}
            keyField="id"
            summaryColumns={[
              {
                field: 'status',
                render: (value: PurchaseLandingRequestStatus) => {
                  const config = STATUS_CONFIG[value];
                  return (
                    <span className={`!px-2 !py-0.5 !rounded !text-xs !font-semibold ${config.bgColor} ${config.textColor}`}>
                      {config.label}
                    </span>
                  );
                },
              },
              {
                field: 'name',
                render: (value, row) => {
                  const names = row.custInfos?.map(c => c.name).join(', ') || value;
                  return <span className="!text-sm !font-medium">{names}</span>;
                },
              },
              {
                field: 'packagePrice',
                render: (value) => <span className="!text-xs !text-blue-600">{value ? `${value.toLocaleString()}원` : '-'}</span>,
              },
            ]}
            detailColumns={[
              { 
                field: 'createdAt', 
                label: '신청일시',
                render: (value) => value ? new Date(value).toLocaleString() : '-',
              },
              { field: 'phone', label: '연락처' },
              { field: 'packageName', label: '패키지', render: (value) => value || '(삭제된 패키지)' },
              { 
                field: 'productNames', 
                label: '상품',
                render: (value: string[]) => value?.join(', ') || '-',
              },
              {
                field: 'campaignName',
                label: '캠페인',
                render: (value, row) => {
                  const display = value || '-';
                  if (!row.landingDeleted) return display;
                  return `${display} (삭제됨)`;
                },
              },
              ...(role === 'ROLE_ADMIN' ? [{ field: 'companyName', label: '가맹점', render: (value: string) => value || '-' }] : []),
            ]}
            emptyMessage="구매 신청내역이 없습니다."
            selectable={true}
            selectedKeys={mobileSelectedKeys}
            onSelectionChange={(keys, rows) => {
              setMobileSelectedKeys(keys);
              setMobileSelectedRows(rows);
            }}
            onRowClick={(row) => handleViewDetail(row)}
            actions={(row) => {
              const { unpaidAlimtalk1SentAt, unpaidAlimtalk2SentAt } = row;
              
              // 2차까지 발송 완료
              if (unpaidAlimtalk1SentAt && unpaidAlimtalk2SentAt) {
                return (
                  <div className="!flex !items-center !gap-1 !text-green-600">
                    <FontAwesomeIcon icon={faCheck} />
                    <FontAwesomeIcon icon={faCheck} />
                    <span className="!text-xs !ml-1">알림 완료</span>
                  </div>
                );
              }
              
              // 1차만 발송 완료 → 재발송 버튼
              if (unpaidAlimtalk1SentAt) {
                return (
                  <div className="!flex !items-center !gap-2">
                    <FontAwesomeIcon icon={faCheck} className="!text-green-500" />
                    <button
                      className="!px-3 !py-1.5 !rounded !text-xs !bg-orange-500 !text-white hover:!bg-orange-600 !flex !items-center !gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendUnpaidAlimtalk(row.id, 'UNPAID2');
                      }}
                    >
                      <FontAwesomeIcon icon={faBell} />
                      재발송
                    </button>
                  </div>
                );
              }
              
              // 미발송 → 발송 버튼
              return (
                <button
                  className="!px-3 !py-1.5 !rounded !text-xs !bg-orange-500 !text-white hover:!bg-orange-600 !flex !items-center !gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendUnpaidAlimtalk(row.id, 'UNPAID1');
                  }}
                >
                  <FontAwesomeIcon icon={faBell} />
                  미입금 알림
                </button>
              );
            }}
          />
        ) : (
          <GridTable<IPurchaseLandingRequest>
            height="100%"
            rowData={requestList}
            columnDefs={columnDefs}
            isLoading={loading}
            rowClassRules={{
              pending_payment_row: (params: RowClassParams) =>
                params.data?.status === 'PENDING_PAYMENT',
              order_created_row: (params: RowClassParams) =>
                params.data?.status === 'ORDER_CREATED',
            }}
          />
        )}
      </div>

      {/* 모바일 액션 버튼 */}
      {isMobile && mobileSelectedRows.length > 0 && (
        <div className="!flex !gap-2 !mt-4">
          {mobileSelectedCount > 0 && (
            <button
              className="!flex-1 !py-3 !rounded !bg-green-600 !text-white !font-medium hover:!bg-green-700 disabled:!bg-gray-400 !flex !items-center !justify-center !gap-2"
              onClick={() => {
                // 모바일용 일괄 입금확인
                const pendingIds = mobileSelectedRows
                  .filter(r => r.status === 'PENDING_PAYMENT')
                  .map(r => r.id);
                if (pendingIds.length === 0) return;
                
                setConfirmModal({
                  isOpen: true,
                  title: '일괄 입금확인',
                  message: `${pendingIds.length}건의 입금을 확인 처리하시겠습니까?`,
                  subMessage: '• 입금완료 알림톡이 함께 발송됩니다.',
                  confirmText: '입금확인',
                  confirmButtonClass: '!bg-green-600 hover:!bg-green-700',
                  onConfirm: async () => {
                    setIsConfirmLoading(true);
                    let successCount = 0;
                    for (const id of pendingIds) {
                      const success = await confirmPayment(id);
                      if (success) successCount++;
                    }
                    setMobileSelectedKeys(new Set());
                    setMobileSelectedRows([]);
                    if (successCount > 0) {
                      await fetchRequests(requestPage?.pageable?.pageNumber ?? 0);
                      toast.success(`${successCount}건 입금확인 완료`);
                    }
                    closeConfirmModal();
                  },
                });
              }}
              disabled={isBulkProcessing}
            >
              <FontAwesomeIcon icon={faCheck} />
              입금확인 ({mobileSelectedCount}건)
            </button>
          )}
          <button
            className="!flex-1 !py-3 !rounded !bg-red-600 !text-white !font-medium hover:!bg-red-700 disabled:!bg-gray-400 !flex !items-center !justify-center !gap-2"
            onClick={() => {
              const ids = mobileSelectedRows.map(r => r.id);
              setConfirmModal({
                isOpen: true,
                title: '일괄 삭제',
                message: `${ids.length}건의 신청을 삭제하시겠습니까?`,
                subMessage: '• 삭제된 신청은 복구할 수 없습니다.',
                confirmText: '삭제',
                confirmButtonClass: '!bg-red-600 hover:!bg-red-700',
                onConfirm: async () => {
                  setIsConfirmLoading(true);
                  let successCount = 0;
                  for (const id of ids) {
                    const success = await deleteRequest(id);
                    if (success) successCount++;
                  }
                  setMobileSelectedKeys(new Set());
                  setMobileSelectedRows([]);
                  if (successCount > 0) {
                    await fetchRequests(requestPage?.pageable?.pageNumber ?? 0);
                    toast.success(`${successCount}건 삭제 완료`);
                  }
                  closeConfirmModal();
                },
              });
            }}
            disabled={isBulkProcessing}
          >
            <FontAwesomeIcon icon={faTrash} />
            삭제 ({mobileSelectedRows.length}건)
          </button>
        </div>
      )}

      {/* 페이지네이션 */}
      {requestPage && requestPage.totalPages > 1 && (
        <div className="!w-full !mt-4">
          <div className="!flex !justify-center !items-center !space-x-2">
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePagination(0)}
              disabled={requestPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePagination(Math.max(0, requestPage.pageable.pageNumber - 1))}
              disabled={requestPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="!text-sm !text-gray-600">
              {requestPage.pageable.pageNumber + 1} / {requestPage.totalPages || 1}
            </span>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePagination(Math.min(requestPage.totalPages - 1, requestPage.pageable.pageNumber + 1))}
              disabled={requestPage.pageable.pageNumber >= requestPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePagination(Math.max(requestPage.totalPages - 1, 0))}
              disabled={requestPage.pageable.pageNumber >= requestPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}

      {/* 상세보기 모달 */}
      {isDetailOpen && selectedRequest && (
        <div className="!fixed !inset-0 !bg-black !bg-opacity-50 !flex !items-center !justify-center !z-50">
          <div className="!bg-white !p-6 !rounded-lg !shadow-xl !w-full !max-w-2xl !max-h-[80vh] !overflow-y-auto">
            <h3 className="!text-lg !font-bold !mb-4">신청 상세 정보</h3>

            <div className="!grid !grid-cols-2 !gap-4 !mb-6">
              <div>
                <label className="!text-sm !text-gray-500">신청일시</label>
                <p className="!font-medium">
                  {new Date(selectedRequest.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="!text-sm !text-gray-500">상태</label>
                <p>
                  <span
                    className={`!px-2 !py-1 !rounded !text-xs !font-semibold ${
                      STATUS_CONFIG[selectedRequest.status].bgColor
                    } ${STATUS_CONFIG[selectedRequest.status].textColor}`}
                  >
                    {STATUS_CONFIG[selectedRequest.status].label}
                  </span>
                </p>
              </div>
              <div>
                <label className="!text-sm !text-gray-500">캠페인</label>
                <p className="!font-medium">
                  {selectedRequest.campaignName}
                  {selectedRequest.landingDeleted && (
                    <span className="!text-xs !text-red-500 !ml-2">(삭제됨)</span>
                  )}
                </p>
              </div>
              <div className="!col-span-2">
                <label className="!text-sm !text-gray-500">패키지 / 상품</label>
                <p className="!font-medium !mb-1">
                  {selectedRequest.packageName ?? '(삭제된 패키지)'} (
                  {selectedRequest.packagePrice != null
                    ? `${selectedRequest.packagePrice.toLocaleString()}원`
                    : '-'}
                  )
                </p>
                {(selectedRequest.productNames ?? []).length > 0 && (
                  <div className="!flex !flex-wrap !gap-1">
                    {selectedRequest.productNames?.map((name, idx) => (
                      <span
                        key={idx}
                        className="!px-2 !py-0.5 !rounded !text-xs !bg-indigo-100 !text-indigo-800"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="!text-sm !text-gray-500">신청자</label>
                <p className="!font-medium">{selectedRequest.name}</p>
              </div>
              <div>
                <label className="!text-sm !text-gray-500">연락처</label>
                <p className="!font-medium">{selectedRequest.phone}</p>
              </div>
              {selectedRequest.paymentConfirmedAt && (
                <div>
                  <label className="!text-sm !text-gray-500">입금확인일시</label>
                  <p className="!font-medium">
                    {new Date(selectedRequest.paymentConfirmedAt).toLocaleString()}
                  </p>
                </div>
              )}
              {selectedRequest.orderCreatedAt && (
                <div>
                  <label className="!text-sm !text-gray-500">주문생성일시</label>
                  <p className="!font-medium">
                    {new Date(selectedRequest.orderCreatedAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* 고객 정보 */}
            <div className="!mb-4">
              <h4 className="!font-semibold !mb-2">고객 정보</h4>
              <div className="!space-y-3">
                {selectedRequest.custInfos?.map((cust, index) => (
                  <div
                    key={cust.id}
                    className="!p-3 !bg-gray-50 !rounded-lg"
                  >
                    <div className="!flex !items-center !gap-2 !mb-2">
                      <span className="!text-sm !font-semibold !text-indigo-600">
                        {index === 0 ? '신청자' : '상대방'}
                      </span>
                    </div>
                    <div className="!grid !grid-cols-3 !gap-2 !text-sm">
                      <div>
                        <span className="!text-gray-500">이름: </span>
                        <span className="!font-medium">{cust.name}</span>
                      </div>
                      <div>
                        <span className="!text-gray-500">성별: </span>
                        <span className="!font-medium">{cust.sexType}</span>
                      </div>
                      <div>
                        <span className="!text-gray-500">양/음력: </span>
                        <span className="!font-medium">{cust.solarType}</span>
                      </div>
                      <div className="!col-span-3">
                        <span className="!text-gray-500">생년월일시: </span>
                        <span className="!font-medium">{cust.birthday}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 알림톡 발송 이력 */}
            <div className="!mb-6">
              <h4 className="!font-semibold !mb-2">알림톡 발송 이력</h4>
              <div className="!text-sm !space-y-1">
                <p>
                  <span className="!text-gray-500">미입금 알림톡 (1차): </span>
                  <span className="!font-medium">
                    {selectedRequest.unpaidAlimtalk1SentAt
                      ? new Date(selectedRequest.unpaidAlimtalk1SentAt).toLocaleString()
                      : '미발송'}
                  </span>
                </p>
                <p>
                  <span className="!text-gray-500">미입금 알림톡 (2차): </span>
                  <span className="!font-medium">
                    {selectedRequest.unpaidAlimtalk2SentAt
                      ? new Date(selectedRequest.unpaidAlimtalk2SentAt).toLocaleString()
                      : '미발송'}
                  </span>
                </p>
                <p>
                  <span className="!text-gray-500">입금완료 알림톡: </span>
                  <span className="!font-medium">
                    {selectedRequest.paidAlimtalkSentAt
                      ? new Date(selectedRequest.paidAlimtalkSentAt).toLocaleString()
                      : '미발송'}
                  </span>
                </p>
              </div>
            </div>

            <div className="!flex !justify-end">
              <button
                className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                onClick={() => {
                  setDetailOpen(false);
                  setSelectedRequest(null);
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 확인 모달 */}
      <PurchaseLandingConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        confirmText={confirmModal.confirmText}
        confirmButtonClass={confirmModal.confirmButtonClass}
        isLoading={isConfirmLoading}
      />
    </div>
  );
};

export default PurchaseLandingRequestPage;
